import {Inject, Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import {USER_ACTIONS} from '../store/user-actions.constant';
import {HttpInterceptorService} from '../../core/services/http-interceptor.service';
import {Ipractitioner} from '../interfaces/practitioner.interface';
import {GeneralStoreEntity} from '@ispm/core/entities/general-store.entity';
import {Observable} from 'rxjs/Observable';
import {of} from 'rxjs/observable/of';
import {IPatient} from '@ispm/user/interfaces/patient.interface';
import {take, map, filter} from 'rxjs/operators';
import {SESSION_MENU_STORAGE_KEY} from '@ispm/shared/services/ui-customization.service';
import {CORE_ACTIONS} from "@ispm/core/constants/core-actions.constant";
import {BrowserCacheService} from "@ispm/core/services/browser-cache.service";
import {ActionCreatorAbstract} from "@ispm/core/classes/action-creator.abstract";
import { AuthenticationService } from './authentication.service';
import 'rxjs/add/operator/filter';

@Injectable()
export class UserActionCreatorService extends ActionCreatorAbstract {

  constructor(
    store$: Store<any>,
    public authService: AuthenticationService,
    private _http: HttpInterceptorService,
    private _browserCacheService: BrowserCacheService,
  ) {
    super(store$, ['users']);
    this.coreStore$ = store$.select('core');
  }

  static organization = '/organization';
  static practitioner = '/practitioner';
  static patient = '/patient';


  private readonly coreStore$: any;

  public initState() {
    this.store.dispatch({ type : USER_ACTIONS.INIT_USER_STATE, payload: { } });
  }

  public updatePatient(id, data) {
    this.store.dispatch({
      type: USER_ACTIONS.UPDATE_PATIENT,
      payload: {
        id,
        data
      }
    });
  }

  public setCurrentPatient(patientData) {
    this.store.dispatch({
      type: USER_ACTIONS.SET_CURRENT_PATIENT,
      payload: {
        patientData
      }
    });
  }

  public removePatient(id) {
    this.store.dispatch({
      type: USER_ACTIONS.REMOVE_PATIENT,
      payload: {
        id
      }
    });
  }

  public clearPatient() {
    this.store.dispatch({
      type: USER_ACTIONS.CLEAR_PATIENT,
      payload: null
    });
  }


  public updatePractitioner(id, data) {
    this.store.dispatch({
      type: USER_ACTIONS.UPDATE_PRACTITIONER,
      payload: {
        id,
        data
      }
    });
  }

  public setBrowserLang(langCode) {
    this.store.dispatch({
      type: USER_ACTIONS.SET_BROWSER_DETECTED_LANG,
      payload: {
        langCode
      }
    });
  }

  public setReloadAdminWorklist(reload) {
    this.store.dispatch({
      type: USER_ACTIONS.SET_RELOAD_ADMIN_WORKLIST,
      payload: {
        reload
      }
    });
  }

  public removePractitioner(id) {
    this.store.dispatch({
      type: USER_ACTIONS.REMOVE_PRACTITIONER,
      payload: {
        id
      }
    });
  }

  public removeNavigationSessionMetaData() {
    sessionStorage.removeItem(SESSION_MENU_STORAGE_KEY);
  }

  /**
   * getting the user organization data
   */
  public getOrganizationConfiguration(orgId: string = null) {
    if (this.authService.getUser()) {
      if (this.isAdminHasPermissionToCreateWorklist() && sessionStorage.getItem('adminSelectOrganizationId') != null) {
        orgId = sessionStorage.getItem('adminSelectOrganizationId');
      }
      orgId = orgId || this.authService.user.organization;
      return this._http.get(`${UserActionCreatorService.organization}/${orgId}`, {cacheId: this._browserCacheService.getCacheId()})
        .map(data => data.json());
    } else {
      return of(null);
    }
  }

  public isAdminHasPermissionToCreateWorklist() {
    const role = this.authService.user.role;
    return (role.toLowerCase() === 'genomicsadmingroup')
      && (this.authService.user.permissions.includes('ADMIN.ORGANIZATION.WORKLIST'))
      && (this.authService.user.permissions.includes('ADMIN.ORGANIZATION.WORKLIST.CREATE'))
  }

  public getOrganizations(...orgIds: string[]): Observable<any> {
    return this._http.get(`${UserActionCreatorService.organization}?_id=${orgIds.join(',')}`)
      .map(data => data.json());

  }

  public getPractitioners(...practitionersIds: string[]): Observable<any> {
    return this._http.get(`${UserActionCreatorService.practitioner}?_id=${practitionersIds.join(',')}`)
      .map(data => data.json());
  }

  public getStore() {
    return this.store;
  }

  // public getOrganizationFromStore():Observable<any>{
  //   return this.store.select('organizationConfiguration');
  // }

  public setCurrentUser(user: Ipractitioner) {
    this.store.dispatch({
      type: USER_ACTIONS.UPDATE_CURRENT,
      payload: GeneralStoreEntity.builder(user)
    });
  }

  /**
   * todo: Adapter, should be replaced by querying the store as soon we set the population function
   */
  public getCurrentUser(): Observable<{
    info: Ipractitioner,
    [prop: string]: any
  }> {
    return this.store.select('currentUser');
  }

  public forceSetOrganizationConfiguration() {
    this.getOrganizationConfiguration()
      .subscribe(config => {
        this.store.dispatch({
          type: USER_ACTIONS.SET_ORGANIZATION_CONFIGURATION,
          payload: config
        });
      });
  }

  public setOrganizationConfiguration() {
    this.store.select('organizationConfiguration')
      .take(1)
      .filter(data => !data)
      .flatMap(config => this.getOrganizationConfiguration())
      .subscribe(config => {
        this.store.dispatch({
          type: USER_ACTIONS.SET_ORGANIZATION_CONFIGURATION,
          payload: config
        });
      });
  }

  public getCurrentUserId(): Observable<string> {
    return Observable.of(this.authService.user.info.id);
  }

  public getPatient(id) {
    return this.getStore().select('patients').pipe(
      map((patientsMap: Map<string, IPatient>) => patientsMap.get(id)),
      take(1)
    );
  }

  public getUserInfo() {
    return Observable.of(this.authService.getUser());
  }

  public loadFromCache() {
    if (!this.authService.user) {
      return;
    }
    const key = `uc-${this.authService.user.info.id}`;
    const state = localStorage.getItem(key);
    return state ? JSON.parse(state) : null;
  }

  public storeCache(value) {
    if (!this.authService.user) {
      return;
    }
    const key = `uc-${this.authService.user.info.id}`;
    let state = localStorage.getItem(key);
    state = state ? JSON.parse(state) : { };
    Object.assign(state, value);
    localStorage.setItem(key, JSON.stringify(state));
  }

  public getStoredOrganizationConfiguration() {
    return this.getStore().select('organizationConfiguration');
  }

  public getBrowserDetectedLanguage() {
    return this.getStore().select('browserDetectedLanguage');
  }

  /**
   * getting patient data based on MRN
   */
  public getPatientByMRN(mrn: string = null) {
    return this._http.get(`${UserActionCreatorService.patient}?mrn=${mrn}`)
      .pipe(
        map(res => res ? res.body[0] : null)
      );

  }

  public setSelectedFacilities(facilityObj) {
    this.store.dispatch({
      type: USER_ACTIONS.UPDATE_SELECTED_FACILITIES,
      payload: facilityObj
    });
  }


  public setSelectedFacility(facilityID) {
    this.store.dispatch({
      type: USER_ACTIONS.SELECTED_FACILITY,
      payload: facilityID
    });
  }

  public getSelectedFacility() {
    return this.getStore().select('selectedFacility');
  }

  public setEnvironmentDetails() {
    this.coreStore$.select('environmentDetails')
      .take(1)
      .filter(data => !data)
      .flatMap(environmentDetails => this.getEnvironmentDetails())
      .subscribe(environmentDetails => {
        this.coreStore$.dispatch({
          type: CORE_ACTIONS.SET_ENVIRONMENT_DETAILS,
          payload: environmentDetails
        });
      });
  }

  public getEnvironmentDetails() {
    return this._http.get('/endpoints/environment-details')
      .map(data => data.json());
  }

}
